import type { EditorialFamily, SlideFigure } from "../contentspec/types.js";
import { numbersOf } from "./numbers.js";
import { announcedCount } from "./style.js";
import { normalizeText } from "./text.js";

/**
 * Editorial families V1 (docs/EDITORIAL_FAMILIES_V1.md).
 *
 * A family is a way of telling a subject. It decides two things the provider
 * does not: the **narrative progression** (which roles, in which order, what
 * each step must carry) and the **composition** of every slide (layout
 * variant, structural number, chronology strip, key figure, split panels).
 *
 * Every family keeps the OPULANCIA signature — a dominant photograph
 * (documentary, archive or spectacular), condensed white text, the gold
 * accent, the black gradient, a discreet brand line — and changes how the
 * pages are built around it:
 *
 *   list        independent items numbered 01…n, full-bleed photos alternating centred / side subject, the last item as finale
 *   ranking     a countdown n…1, rank badge + measured value on every step, N°1 as the payoff in impact
 *   story       dated chapters on a timeline strip, subject on the side, the reveal late and in impact
 *   comparison  pairs side by side (two photo panels with their values: before / after, montage), then the verdict
 *
 * Pure: the same outline always gets the same compositions.
 */
export const FAMILIES = ["list", "ranking", "story", "comparison"] as const satisfies readonly EditorialFamily[];
export type Family = EditorialFamily;

export type SlideRole = "hook" | "setup" | "reveal" | "proof" | "escalation" | "contrast" | "payoff" | "cta";
export type SlideVariant = "standard" | "impact" | "offset" | "split" | "card";

/** Roles that state facts (the items / steps / chapters of a family). */
export const FACT_ROLES: readonly SlideRole[] = ["reveal", "proof", "escalation", "contrast", "payoff"];

export interface FamilyInfo {
  id: Family;
  /** French label, shown to editors. */
  label: string;
  /** Default cover kicker when the outline gives none. */
  coverKicker: string;
  /** One sentence: how the family progresses. */
  progression: string;
}

export const FAMILY_INFO: Record<Family, FamilyInfo> = {
  list: {
    id: "list",
    label: "Liste / objets",
    coverKicker: "La liste",
    progression: "des éléments indépendants numérotés 01…n, un par slide ; le dernier sert de finale",
  },
  ranking: {
    id: "ranking",
    label: "Classement / records",
    coverKicker: "Le classement",
    progression: "un compte à rebours n…1 ; chaque marche porte sa valeur mesurée ; le N°1 est le payoff",
  },
  story: {
    id: "story",
    label: "Histoire / révélation",
    coverKicker: "L’histoire",
    progression: "des chapitres datés sur une frise ; la tension monte ; la révélation arrive tard, en pleine image",
  },
  comparison: {
    id: "comparison",
    label: "Comparaison / évolution",
    coverKicker: "Avant / après",
    progression: "des paires côte à côte (deux panneaux et leurs valeurs), puis le verdict",
  },
};

/** A slide as the family sees it (outline or plan): only what composition needs. */
export interface FamilySlide {
  role: SlideRole;
  type?: string;
  title: string;
  subtitle?: string;
  kicker?: string;
  variant?: SlideVariant;
  figure?: SlideFigure;
  panels?: Array<{ asset_id?: string | null; label: string; figure?: SlideFigure }>;
  /** Story / evolution: when the slide happens ("1887"); builds the timeline. */
  when?: string;
}

/** What the family adds to a slide. The provider's own choices (variant, kicker) win when given. */
export interface Composition {
  composition: string;
  variant?: SlideVariant;
  kicker?: string | undefined;
  marker?: { value: string; style: "outline" | "solid" };
  timeline?: { steps: string[]; current: number } | undefined;
}

const isFact = (slide: FamilySlide) => FACT_ROLES.includes(slide.role);

/**
 * The timeline is optional and discreet: drawn only when the chronology really
 * helps, i.e. the slides are dated (`when`) at 3 or more distinct moments.
 * Otherwise the date stays a kicker.
 */
export const MIN_TIMELINE_STEPS = 3;

/**
 * Compositions of every slide of an outline, in order. Hook and cta keep their
 * own layouts; the family composes the slides in between.
 */
export function composeFamily(family: Family, slides: FamilySlide[]): Composition[] {
  const facts = slides.filter(isFact);
  const factIndex = (slide: FamilySlide) => facts.indexOf(slide);
  // story / comparison: the distinct `when` values, in reading order, form the timeline
  const steps = [...new Set(slides.map((s) => s.when?.trim()).filter((w): w is string => !!w))].slice(0, 6);
  const timelineOf = (slide: FamilySlide) =>
    slide.when?.trim() && steps.length >= MIN_TIMELINE_STEPS ? { steps, current: Math.max(0, steps.indexOf(slide.when.trim())) } : undefined;
  let impacts = 0;
  let listCount = 0;

  return slides.map((slide) => {
    if (slide.role === "hook") return { composition: `${family}-cover`, kicker: slide.kicker ?? FAMILY_INFO[family].coverKicker };
    if (slide.role === "cta") return { composition: `${family}-cta` };
    const k = factIndex(slide);
    const last = k === facts.length - 1;
    const timeline = timelineOf(slide);
    // the date is shown once: on the timeline when there is one, else as the kicker
    const kicker = slide.kicker ?? (!timeline && slide.when ? slide.when : undefined);
    switch (family) {
      case "list": {
        if (!isFact(slide)) return { composition: "list-intro", variant: slide.variant ?? "standard", kicker };
        listCount += 1;
        const marker = { value: String(listCount), style: "outline" as const };
        if (last && slide.role === "payoff") return { composition: "list-finale", variant: slide.variant ?? "impact", marker, kicker };
        // photo-led rhythm: full-bleed subject, then subject on the side, alternating.
        // `card` (framed image) stays available, but only when the provider asks for it.
        const variant = slide.variant ?? (listCount % 2 === 1 ? "standard" : "offset");
        return { composition: variant === "card" ? "list-card" : "list-item", variant, marker, kicker };
      }
      case "ranking": {
        if (!isFact(slide)) return { composition: "ranking-criterion", variant: slide.variant ?? "standard", kicker };
        const rank = facts.length - k;
        const marker = { value: String(rank), style: "solid" as const };
        if (rank === 1) return { composition: "ranking-top", variant: slide.variant ?? "impact", marker, kicker };
        return { composition: "ranking-step", variant: slide.variant ?? "standard", marker, kicker };
      }
      case "story": {
        if (slide.role === "setup") return { composition: "story-setup", variant: slide.variant ?? "standard", kicker, timeline };
        if (slide.role === "reveal" && impacts < 1) {
          impacts += 1;
          return { composition: "story-reveal", variant: slide.variant ?? "impact", kicker, timeline };
        }
        if (slide.role === "payoff") return { composition: "story-payoff", variant: slide.variant ?? "standard", kicker, timeline };
        // a key figure needs the full width: in the 428 px offset column a 6-digit value forces a fallback (Golden Test V1.1)
        return { composition: "story-chapter", variant: slide.variant ?? (slide.figure ? "standard" : "offset"), kicker, timeline };
      }
      case "comparison": {
        if (slide.role === "contrast" || slide.panels?.length === 2) {
          return { composition: "comparison-split", variant: "split", kicker, timeline };
        }
        if (slide.role === "payoff") return { composition: "comparison-verdict", variant: slide.variant ?? "impact", kicker, timeline };
        return { composition: "comparison-step", variant: slide.variant ?? "standard", kicker, timeline };
      }
    }
  });
}

/** Roles of n fact slides for a family (used by providers without their own structure, e.g. the mock). */
export function familyRoles(family: Family, count: number): SlideRole[] {
  if (count <= 0) return [];
  if (count === 1) return ["payoff"];
  switch (family) {
    case "list":
    case "ranking":
      return [...Array<SlideRole>(count - 1).fill("escalation"), "payoff"];
    case "story":
      if (count === 2) return ["reveal", "payoff"];
      return ["setup", ...Array<SlideRole>(count - 3).fill("escalation"), "reveal", "payoff"];
    case "comparison":
      return [...Array<SlideRole>(count - 1).fill("contrast"), "payoff"];
  }
}

// ---- selection -------------------------------------------------------------------

export interface FamilyChoice {
  id: Family;
  source: "requested" | "inferred";
  reason: string;
}

/**
 * Subject patterns, in priority order (normalised text: lowercase, no accents).
 * A subject that announces itself as a story (« l’histoire du… record ») is a
 * story first: the narrative words win over the record they lead to.
 */
const SIGNALS: Array<{ family: Family; pattern: RegExp; label: string }> = [
  { family: "list", pattern: /\b\d+ (objets|choses|faits|chiffres|raisons|lieux|details|secrets|inventions)\b|\bliste\b/, label: "une liste d’éléments" },
  { family: "story", pattern: /\b(histoire|secret|devait|a failli|origine|pourquoi|comment|mystere|affaire|jour ou)\b/, label: "une histoire / révélation" },
  { family: "ranking", pattern: /\b(classement|record|records|top \d+|les \d+ plus|plus (haut|hauts|haute|hautes|grand|grands|grande|grandes|cher|chers|chere|cheres|rapide|rapides|vieux|vieille|riche|riches))\b/, label: "un classement / record" },
  { family: "comparison", pattern: /\b(avant|apres|vs|versus|contre|evolution|comparaison|compare|hier|aujourd hui|a grandi|a change)\b/, label: "une comparaison / évolution" },
];

/**
 * The family of a request: the requested one, else inferred from the subject;
 * without any signal, a list when there are at least 4 usable claims, else a story.
 * The reason is recorded in the plan.
 */
export function selectFamily(requested: Family | undefined, subject: string, usableClaims: number): FamilyChoice {
  if (requested) return { id: requested, source: "requested", reason: `famille demandée par la requête (${FAMILY_INFO[requested].label})` };
  const text = normalizeText(subject);
  for (const signal of SIGNALS) {
    const match = text.match(signal.pattern);
    if (match) return { id: signal.family, source: "inferred", reason: `le sujet annonce ${signal.label} (« ${match[0]} »)` };
  }
  return usableClaims >= 4
    ? { id: "list", source: "inferred", reason: `aucun signal dans le sujet ; ${usableClaims} faits utilisables → liste` }
    : { id: "story", source: "inferred", reason: `aucun signal dans le sujet ; ${usableClaims} faits utilisables → histoire` };
}

// ---- progression checks (editorial QA, code FAMILY_PROGRESSION) ---------------------

export interface ProgressionSlide {
  index: number;
  role?: string;
  title: string;
  variant?: string;
  marker?: { value: string; style: string };
  figure?: SlideFigure;
  panels?: Array<{ label: string; figure?: SlideFigure }>;
  timeline?: { steps: string[]; current: number };
}

export interface ProgressionIssue {
  level: "warning" | "blocking";
  message: string;
  slide: ProgressionSlide | null;
}

const numeric = (figure: SlideFigure | undefined) => {
  const n = figure ? numbersOf(figure.value)[0] : undefined;
  return n === undefined ? null : Number(n);
};

/**
 * Which progression checks run. `all` (default): every check. `narrative`:
 * only the checks that read slide roles (the story's reveal). The others read
 * fields the renderer composes and draws (list markers, ranking values,
 * timeline strip, split panels); a carousel made by hand carries none of them,
 * so they cannot be measured on it (editorial QA profile `manual`).
 */
export type ProgressionScope = "all" | "narrative";

/**
 * Whether the slides follow their family's progression. Blocking when the
 * structure would mislead the reader (a list numbered out of order, a
 * ranking that does not count down or states no value, a comparison with
 * nothing side by side, a story without its reveal); warnings otherwise.
 */
export function progressionIssues(family: Family, slides: ProgressionSlide[], hookTitle: string, scope: ProgressionScope = "all"): ProgressionIssue[] {
  const issues: ProgressionIssue[] = [];
  if (scope === "narrative" && family !== "story") return issues;
  const add = (level: ProgressionIssue["level"], message: string, slide: ProgressionSlide | null = null) => issues.push({ level, message, slide });
  const facts = slides.filter((s) => FACT_ROLES.includes(s.role as SlideRole));
  switch (family) {
    case "list": {
      const numbered = slides.filter((s) => s.marker);
      numbered.forEach((slide, i) => {
        if (slide.marker!.value !== String(i + 1)) add("blocking", `list item numbered ${slide.marker!.value}, expected ${i + 1} (items are numbered 1…n in reading order)`, slide);
      });
      if (numbered.length < 3) add("warning", `a list needs at least 3 numbered items (found ${numbered.length})`);
      // a hook that announces a count promises at least that many items; a hook without a count is fine
      // (calibrated on the library: 3 of 7 published lists announce no count, docs/EDITORIAL_CALIBRATION.md)
      const items = numbered.length || facts.length;
      const announced = announcedCount(hookTitle);
      if (announced !== null && items < announced) add("blocking", `the hook announces ${announced} items, the list has ${items}`);
      else if (announced !== null && items > announced) add("warning", `the hook announces ${announced} items, the list has ${items} (a bonus?)`);
      break;
    }
    case "ranking": {
      const ranked = slides.filter((s) => s.marker);
      ranked.forEach((slide, i) => {
        const expected = String(ranked.length - i);
        if (slide.marker!.value !== expected) add("blocking", `rank ${slide.marker!.value} out of order; a ranking counts down ${ranked.length}…1 (expected ${expected})`, slide);
        if (!slide.figure) add("blocking", `rank ${slide.marker!.value} states no measured value (figure); a ranking without values is only a list`, slide);
      });
      if (ranked.length < 3) add("warning", `a ranking needs at least 3 ranks (found ${ranked.length})`);
      const values = ranked.map((s) => numeric(s.figure));
      const units = new Set(ranked.map((s) => s.figure?.unit ?? ""));
      if (units.size === 1 && values.every((v) => v !== null)) {
        const up = values.every((v, i) => i === 0 || v! >= values[i - 1]!);
        const down = values.every((v, i) => i === 0 || v! <= values[i - 1]!);
        if (!up && !down) add("warning", `values do not move in one direction along the countdown (${values.join(" → ")})`);
      }
      break;
    }
    case "story": {
      const body = slides.filter((s) => s.role !== "hook" && s.role !== "cta");
      const reveals = body.filter((s) => s.role === "reveal");
      if (!reveals.length) add("blocking", "a story needs a reveal slide (the fact that changes the understanding)");
      const firstReveal = reveals[0];
      if (firstReveal && body.indexOf(firstReveal) < (body.length - 1) / 2) {
        add("warning", `the reveal comes early (slide ${firstReveal.index}); a story keeps it for the second half`, firstReveal);
      }
      if (scope === "narrative") break;
      let previous = -1;
      for (const slide of slides.filter((s) => s.timeline)) {
        if (slide.timeline!.current < previous) add("warning", `the timeline goes back in time on slide ${slide.index} (flashback)`, slide);
        previous = slide.timeline!.current;
      }
      break;
    }
    case "comparison": {
      const splits = slides.filter((s) => s.variant === "split");
      if (!splits.length) add("blocking", "a comparison needs at least one split slide (two panels side by side)");
      for (const slide of splits) {
        if (slide.panels?.length !== 2) add("blocking", `split slide ${slide.index} needs exactly two panels`, slide);
        else if (!!slide.panels[0]!.figure !== !!slide.panels[1]!.figure) add("warning", `split slide ${slide.index}: only one side states a value`, slide);
      }
      break;
    }
  }
  return issues;
}
